export type WorkflowApprovalStageItem<T> = {
  step: T;
  index: number;
};

export type WorkflowApprovalStageGroup<T> = {
  stageOrder: number;
  items: WorkflowApprovalStageItem<T>[];
};

export function normalizeWorkflowApprovalSteps<T extends { order: number; stageOrder: number }>(
  steps: readonly T[],
): T[] {
  const hasInvalidStageOrder = steps.some((step) => (
    !Number.isFinite(step.stageOrder) || step.stageOrder <= 0
  ));
  if (hasInvalidStageOrder) {
    return steps.map((step, index) => ({ ...step, order: index + 1 }));
  }

  let stageOrder = 0;
  let previousStageOrder: number | undefined;
  return steps.map((step, index) => {
    if (index === 0 || step.stageOrder !== previousStageOrder) stageOrder += 1;
    previousStageOrder = step.stageOrder;
    return { ...step, order: index + 1, stageOrder };
  });
}

export function workflowApprovalStageSequenceIsValid(
  steps: readonly { stageOrder: number }[],
): boolean {
  let previousStageOrder = 0;
  return steps.every((step, index) => {
    const valid = index === 0
      ? step.stageOrder === 1
      : step.stageOrder === previousStageOrder || step.stageOrder === previousStageOrder + 1;
    previousStageOrder = step.stageOrder;
    return valid;
  });
}

export function groupWorkflowApprovalSteps<T extends { stageOrder: number }>(
  steps: readonly T[],
): WorkflowApprovalStageGroup<T>[] {
  const groups: WorkflowApprovalStageGroup<T>[] = [];
  steps.forEach((step, index) => {
    const current = groups[groups.length - 1];
    if (!current || current.stageOrder !== step.stageOrder) {
      groups.push({ stageOrder: step.stageOrder, items: [{ step, index }] });
      return;
    }
    current.items.push({ step, index });
  });
  return groups;
}

export function mergeWorkflowApprovalStageWithPrevious<T extends { stageOrder: number }>(
  steps: readonly T[],
  stageOrder: number,
): T[] {
  if (stageOrder <= 1) return steps.map((step) => ({ ...step }));
  return steps.map((step) => step.stageOrder === stageOrder
    ? { ...step, stageOrder: stageOrder - 1 }
    : { ...step });
}

export function splitWorkflowApprovalStageAt<T extends { stageOrder: number }>(
  steps: readonly T[],
  index: number,
): T[] {
  const stageOrder = steps[index]?.stageOrder;
  if (stageOrder === undefined) return steps.map((step) => ({ ...step }));
  return steps.map((step, current) => current >= index && step.stageOrder === stageOrder
    ? { ...step, stageOrder: stageOrder + 0.5 }
    : { ...step });
}

export function insertWorkflowApprovalStepInStage<T extends { stageOrder: number }>(
  steps: readonly T[],
  stageOrder: number,
  step: T,
): T[] {
  let insertIndex = steps.length;
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (steps[index].stageOrder === stageOrder) {
      insertIndex = index + 1;
      break;
    }
  }
  const next = steps.map((item) => ({ ...item }));
  next.splice(insertIndex, 0, { ...step, stageOrder });
  return next;
}

export function moveWorkflowApprovalStage<T extends { stageOrder: number }>(
  steps: readonly T[],
  stageOrder: number,
  direction: -1 | 1,
): T[] {
  const groups = groupWorkflowApprovalSteps(steps);
  const sourceIndex = groups.findIndex((group) => group.stageOrder === stageOrder);
  const targetIndex = sourceIndex + direction;
  if (sourceIndex < 0 || targetIndex < 0 || targetIndex >= groups.length) {
    return steps.map((step) => ({ ...step }));
  }
  [groups[sourceIndex], groups[targetIndex]] = [groups[targetIndex], groups[sourceIndex]];
  return groups.flatMap((group) => group.items.map(({ step }) => ({ ...step })));
}
